// src/pages/Submit.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiAlertCircle, FiCheckCircle, FiLoader } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function Submit() {
  const [formData, setFormData] = useState({
    question: '',
    submitted_by: '',
    submitted_by_email: '',
    category: 'General FAQ'
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Load categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/questions/categories/list`);
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Use default categories if fetch fails
      setCategories([
        { id: 1, name: 'Installation' },
        { id: 2, name: 'Maintenance & Repair' },
        { id: 3, name: 'Commercial Services' },
        { id: 4, name: 'Safety & Troubleshooting' },
        { id: 5, name: 'Warranty & Service' },
        { id: 6, name: 'General FAQ' }
      ]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/questions`, {
        question: formData.question,
        submitted_by: formData.submitted_by || 'Anonymous',
        submitted_by_email: formData.submitted_by_email || null,
        category: formData.category
      });

      setResult(response.data);
      setFormData(prev => ({
        ...prev,
        question: ''
      }));

      // Show result for 5 seconds then clear
      setTimeout(() => {
        setResult(null);
      }, 5000);

    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
      console.error('Error submitting question:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Ask a Question</h2>
        <p className="text-gray-600">
          Have a question about garage doors or our services? Submit it below and our team will provide an answer.
        </p>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
          <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {result && result.isDuplicate && result.answer && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex gap-3 mb-3">
            <FiCheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-green-700 font-medium">Great news! We found an answer!</p>
          </div>
          <div className="bg-white p-4 rounded border border-green-100 mt-3">
            <p className="text-gray-900"><strong>Your question:</strong> {formData.question || result.message}</p>
            <p className="text-gray-700 mt-3"><strong>Answer:</strong></p>
            <p className="text-gray-700 mt-2">{result.answer}</p>
          </div>
        </div>
      )}

      {result && !result.isDuplicate && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex gap-3">
          <FiCheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-blue-700 font-medium">Question submitted!</p>
            <p className="text-blue-600 text-sm mt-1">Our team will answer it shortly. You'll receive a notification when it's answered.</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
        {/* Question Field */}
        <div className="mb-6">
          <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
            Your Question <span className="text-red-500">*</span>
          </label>
          <textarea
            id="question"
            name="question"
            value={formData.question}
            onChange={handleChange}
            placeholder="Ask your question about garage doors, installation, maintenance, services, or anything related to AK Doors..."
            rows="5"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            required
            disabled={loading}
          />
          <p className="text-gray-500 text-sm mt-1">
            {formData.question.length} characters
          </p>
        </div>

        {/* Name Field */}
        <div className="mb-6">
          <label htmlFor="submitted_by" className="block text-sm font-medium text-gray-700 mb-2">
            Your Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="submitted_by"
            name="submitted_by"
            value={formData.submitted_by}
            onChange={handleChange}
            placeholder="Your name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
            disabled={loading}
          />
        </div>

        {/* Email Field */}
        <div className="mb-6">
          <label htmlFor="submitted_by_email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="submitted_by_email"
            name="submitted_by_email"
            value={formData.submitted_by_email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            disabled={loading}
          />
          <p className="text-gray-500 text-sm mt-1">Required — we'll notify you in Slack (matched by your email) when your question is answered.</p>
        </div>

        {/* Category Field */}
        <div className="mb-6">
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            disabled={loading}
          >
            {categories.map(cat => (
              <option key={cat.id || cat.name} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !formData.question.trim()}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <FiLoader className="animate-spin" size={20} />
              Submitting...
            </>
          ) : (
            'Submit Question'
          )}
        </button>
      </form>

      {/* Helpful Tips */}
      <div className="mt-8 bg-indigo-50 border border-indigo-200 rounded-lg p-6">
        <h3 className="font-semibold text-indigo-900 mb-3">💡 Tips for better answers:</h3>
        <ul className="text-indigo-800 space-y-2 text-sm">
          <li>• Be specific about your situation (model, age of door, symptoms)</li>
          <li>• Include relevant details (when did the problem start, what have you tried)</li>
          <li>• Ask one question at a time</li>
          <li>• Use the correct category for faster routing</li>
        </ul>
      </div>
    </div>
  );
}

export default Submit;
